import type { MessageKey } from '@dijitalapp/i18n';
import type { ContactDto, ConversationDto, GroupMemberDto } from '@dijitalapp/shared';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChatStore } from '../lib/chat-context';
import { GroupLinkAdmin } from './GroupLinks';

function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  // No close() in cleanup: under StrictMode the effect runs twice, and a queued "close" event from the
  // first cleanup would fire onClose right after opening. Unmounting removes the dialog anyway.
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  return (
    <dialog ref={ref} className="dialog" aria-label={title} onClose={onClose}>
      <div className="stack">
        <h2>{title}</h2>
        {children}
      </div>
    </dialog>
  );
}

function useContacts(): ContactDto[] {
  const [contacts, setContacts] = useState<ContactDto[]>([]);
  useEffect(() => {
    void api<{ contacts: ContactDto[] }>('GET', '/contacts').then((r) => setContacts(r.contacts.filter((c) => !c.blocked)), () => undefined);
  }, []);
  return contacts;
}

function ContactPicker({ contacts, selected, onToggle }: { contacts: ContactDto[]; selected: Set<string>; onToggle: (id: string) => void }) {
  const { i18n } = useApp();
  if (contacts.length === 0) return <p className="muted">{i18n.t('contacts.empty')}</p>;
  return (
    <div className="stack" style={{ gap: 'var(--space-2)', maxHeight: 240, overflowY: 'auto' }}>
      {contacts.map((c) => (
        <label key={c.id} className="check">
          <input type="checkbox" checked={selected.has(c.id)} onChange={() => onToggle(c.id)} />
          <span>
            {c.displayName} <span className="muted">@{c.username}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

/** Shown after adding people: those whose "who can add me to groups" setting did not allow it. */
function skippedNote(t: ReturnType<typeof useApp>['i18n']['t'], contacts: ContactDto[], skipped: string[]): string | null {
  if (skipped.length === 0) return null;
  const names = skipped.map((id) => contacts.find((c) => c.id === id)?.displayName ?? id.slice(0, 8)).join(', ');
  return t('group.skipped', { names });
}

export function NewGroupForm() {
  const { i18n } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const navigate = useNavigate();
  const contacts = useContacts();
  const [title, setTitle] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<string | null>(null);

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setStatus(null);
    try {
      const res = await api<{ conversation: ConversationDto; skipped: string[] }>('POST', '/groups', { title, memberIds: [...selected] });
      await store?.loadConversations();
      const note = skippedNote(t, contacts, res.skipped);
      if (note) window.alert(note);
      navigate(`/messages/${res.conversation.id}`);
    } catch (err) {
      setStatus(t(errorMessageKey(err)));
    }
  };

  return (
    <form className="card stack" onSubmit={(e) => void create(e)} aria-labelledby="new-group-title">
      <h2 id="new-group-title">{t('group.new')}</h2>
      <div className="field">
        <label htmlFor="group-title">{t('group.title')}</label>
        <input id="group-title" className="input" required maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <p className="muted">{t('group.pickMembers')}</p>
      <ContactPicker contacts={contacts} selected={selected} onToggle={toggle} />
      <p className="hint">{t('group.addHint')}</p>
      {status && (
        <p className="alert alert-error" role="alert">
          {status}
        </p>
      )}
      <div className="row">
        <span className="spacer" />
        <button className="btn btn-primary" disabled={!title.trim()}>
          {t('group.create')}
        </button>
      </div>
    </form>
  );
}

export function GroupInfoDialog({
  conversation,
  members,
  onChanged,
  onClose,
}: {
  conversation: ConversationDto;
  members: GroupMemberDto[];
  onChanged: () => void;
  onClose: () => void;
}) {
  const { i18n, me } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const navigate = useNavigate();
  const contacts = useContacts();
  const group = conversation.group!;
  const isOwner = group.myRole === 'owner';
  const isAdmin = isOwner || group.myRole === 'admin';
  const [title, setTitle] = useState(group.title);
  const [description, setDescription] = useState(group.description ?? '');
  const [toAdd, setToAdd] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const memberIds = new Set(members.map((m) => m.id));
  const addable = contacts.filter((c) => !memberIds.has(c.id));

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setStatus(null);
    try {
      await fn();
      onChanged();
      await store?.refreshConversation(conversation.id);
      if (ok) setStatus({ kind: 'ok', text: ok });
    } catch (err) {
      setStatus({ kind: 'error', text: t(errorMessageKey(err)) });
    }
  };

  const leave = async () => {
    if (!window.confirm(t('group.leaveConfirm'))) return;
    try {
      await api('DELETE', `/groups/${conversation.id}/members/me`);
      await store?.loadConversations();
      navigate('/messages', { replace: true });
    } catch (err) {
      setStatus({ kind: 'error', text: t(errorMessageKey(err)) });
    }
  };

  return (
    <Dialog title={t('group.info')} onClose={onClose}>
      {isAdmin ? (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => api('PATCH', `/groups/${conversation.id}`, { title, description: description.trim() || null }), t('privacy.saved'));
          }}
        >
          <div className="field">
            <label htmlFor="gi-title">{t('group.title')}</label>
            <input id="gi-title" className="input" required maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="gi-desc">{t('group.description')}</label>
            <textarea id="gi-desc" className="input" rows={2} maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} style={{ paddingBlock: 'var(--space-2)' }} />
          </div>
          <label className="check">
            <input
              type="checkbox"
              checked={group.onlyAdminsCanSend}
              onChange={(e) => void run(() => api('PATCH', `/groups/${conversation.id}`, { onlyAdminsCanSend: e.target.checked }))}
            />
            <span>{t('group.onlyAdmins')}</span>
          </label>
          <div className="row">
            <span className="spacer" />
            <button className="btn">{t('common.save')}</button>
          </div>
        </form>
      ) : (
        group.description && <p>{group.description}</p>
      )}

      <h3>{t('group.memberCount', { count: members.length })}</h3>
      <div className="stack" style={{ gap: 0 }}>
        {members.map((m) => (
          <div key={m.id} className="toggle-row">
            <div style={{ minWidth: 0 }}>
              <strong>{m.id === me?.id ? t('chat.you') : m.displayName}</strong> <span className="muted">@{m.username}</span>{' '}
              {m.role !== 'member' && <span className="badge">{t(`group.role.${m.role}` as MessageKey)}</span>}
            </div>
            <span className="spacer" />
            {isOwner && m.role !== 'owner' && (
              <button
                className="link-button"
                type="button"
                onClick={() => void run(() => api('PUT', `/groups/${conversation.id}/members/${m.id}/role`, { role: m.role === 'admin' ? 'member' : 'admin' }))}
              >
                {m.role === 'admin' ? t('group.makeMember') : t('group.makeAdmin')}
              </button>
            )}
            {m.id !== me?.id && m.role !== 'owner' && (isOwner || (isAdmin && m.role === 'member')) && (
              <button className="link-button" type="button" onClick={() => void run(() => api('DELETE', `/groups/${conversation.id}/members/${m.id}`))}>
                {t('group.remove')}
              </button>
            )}
          </div>
        ))}
      </div>

      {isAdmin && addable.length > 0 && (
        <details>
          <summary>{t('group.addMembers')}</summary>
          <div className="stack" style={{ marginTop: 'var(--space-3)' }}>
            <ContactPicker
              contacts={addable}
              selected={toAdd}
              onToggle={(id) =>
                setToAdd((s) => {
                  const next = new Set(s);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                })
              }
            />
            <button
              className="btn btn-primary"
              type="button"
              disabled={toAdd.size === 0}
              onClick={() => {
                let skipped: string[] = [];
                void run(async () => {
                  skipped = (await api<{ skipped: string[] }>('POST', `/groups/${conversation.id}/members`, { userIds: [...toAdd] })).skipped;
                  setToAdd(new Set());
                }).then(() => {
                  const note = skippedNote(t, contacts, skipped);
                  if (note) setStatus({ kind: 'error', text: note });
                });
              }}
            >
              {t('group.add')}
            </button>
            <p className="hint">{t('group.addHint')}</p>
          </div>
        </details>
      )}

      {isAdmin && <GroupLinkAdmin conversation={conversation} onChanged={onChanged} />}

      {status && (
        <p role="status" className={status.kind === 'error' ? 'alert alert-error' : 'muted'}>
          {status.text}
        </p>
      )}
      <div className="row">
        <button className="btn btn-danger" type="button" onClick={() => void leave()}>
          {t('group.leave')}
        </button>
        <span className="spacer" />
        <button className="btn" type="button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Dialog>
  );
}
